import { useCallback, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../i18n/useTranslation';
import { Card } from '../../atoms/Card/Card';
import { Input } from '../../atoms/Input/Input';
import { Select } from '../../atoms/Select/Select';
import { Button } from '../../atoms/Button/Button';
import { Heading } from '../../atoms/Typography/Heading';
import { Text } from '../../atoms/Typography/Text';
import { Inline } from '../../atoms/Inline/Inline';
import { Stack } from '../../atoms/Stack/Stack';
import { Alert } from '../../atoms/Alert/Alert';
import { LiveRegion } from '../../atoms/LiveRegion/LiveRegion';
import { EmptyState } from '../../atoms/EmptyState';
import { Icon } from '../../atoms/Icon/Icon';
import { LoadErrorState } from '../../feedback/LoadErrorState/LoadErrorState';
import { SyncLog } from '../SyncLog/SyncLog';
import type { SyncLogEntry } from '../SyncLog/SyncLog';
import { syncFaunaMarinAnalyses } from '../../../services/icpAnalysisService';
import {
  FAUNA_MARIN_PROVIDER,
  FAUNA_MARIN_TOKEN_LENGTH,
  FAUNA_MARIN_TOKEN_URL,
  deleteIntegrationCredentials,
  getIntegrationCredentialsStatus,
  saveIntegrationToken,
  validateFaunaMarinToken,
  verifyIntegrationConnection,
  type IntegrationLastSync,
} from '../../../services/integrationCredentialsService';
import { useToast } from '../../../stores/toastStore';
import { describeFaunaMarinError, toFaunaMarinUiError, type FaunaMarinUiError } from './faunaMarinMessages';
import './FaunaMarinPanel.css';

// The query keys and cached values only ever hold the status / the connected
// account — never the token.
export const FAUNA_MARIN_STATUS_QUERY_KEY = ['integrationCredentialsStatus', FAUNA_MARIN_PROVIDER] as const;
export const FAUNA_MARIN_CONNECTION_QUERY_KEY = ['integrationConnection', FAUNA_MARIN_PROVIDER] as const;

export interface FaunaMarinPanelProps {
  /** ReefGuide aquarium the analyses are imported into. */
  aquariumId: string | undefined;
}

function formatDateTime(iso: string, language: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(language, { dateStyle: 'medium', timeStyle: 'short' });
}

export function FaunaMarinPanel({ aquariumId }: FaunaMarinPanelProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const tokenInputId = useId();

  // The token lives only in this component's state: never in a query key, the
  // query cache or web storage. It is cleared after saving and discarded with
  // the component on unmount.
  const [tokenInput, setTokenInput] = useState('');
  const [tokenTouched, setTokenTouched] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [legacyNotice, setLegacyNotice] = useState(false);
  const [aquariumChoice, setAquariumChoice] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<FaunaMarinUiError | null>(null);
  const [logEntries, setLogEntries] = useState<SyncLogEntry[]>([]);

  const statusQuery = useQuery({
    queryKey: FAUNA_MARIN_STATUS_QUERY_KEY,
    queryFn: async () => {
      const status = await getIntegrationCredentialsStatus(FAUNA_MARIN_PROVIDER);
      // The backend reports the deletion of legacy email/password credentials
      // exactly once — keep the notice for this visit.
      if (status.legacyCredentialsRemoved) setLegacyNotice(true);
      return status;
    },
  });

  const status = statusQuery.data;
  const hasToken = status?.saved === true;
  const partnerKeyConfigured = status?.partnerKeyConfigured !== false;

  const connectionQuery = useQuery({
    queryKey: FAUNA_MARIN_CONNECTION_QUERY_KEY,
    queryFn: () => verifyIntegrationConnection(FAUNA_MARIN_PROVIDER),
    enabled: hasToken && partnerKeyConfigured,
    retry: false,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  const connection = hasToken && connectionQuery.data?.account ? connectionQuery.data : undefined;
  const connectionError = hasToken && connectionQuery.isError ? toFaunaMarinUiError(connectionQuery.error) : null;

  const saveMutation = useMutation({
    mutationFn: (token: string) => saveIntegrationToken(FAUNA_MARIN_PROVIDER, token),
    onSuccess: () => {
      setTokenInput('');
      setTokenTouched(false);
      setShowToken(false);
      setReplacing(false);
      setLegacyNotice(false);
      setAquariumChoice(null);
      queryClient.removeQueries({ queryKey: FAUNA_MARIN_CONNECTION_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FAUNA_MARIN_STATUS_QUERY_KEY });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteIntegrationCredentials(FAUNA_MARIN_PROVIDER),
    onSuccess: () => {
      setReplacing(false);
      setAquariumChoice(null);
      setSyncError(null);
      queryClient.removeQueries({ queryKey: FAUNA_MARIN_CONNECTION_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FAUNA_MARIN_STATUS_QUERY_KEY });
    },
  });

  const appendLog = useCallback((level: SyncLogEntry['level'], message: string) => {
    setLogEntries((prev) => [...prev, { id: crypto.randomUUID(), level, message, timestamp: new Date() }]);
  }, []);

  // ── Token form ────────────────────────────────────────────────────────────
  const validation = validateFaunaMarinToken(tokenInput);
  const tokenError = tokenTouched && validation !== 'ok'
    ? validation === 'empty'
      ? t('integrations.faunaMarin.token.required')
      : validation === 'characters'
        ? t('integrations.faunaMarin.token.invalidCharacters')
        : t('integrations.faunaMarin.token.invalidLength', { expected: FAUNA_MARIN_TOKEN_LENGTH, count: tokenInput.trim().length })
    : undefined;
  const showTokenForm = !hasToken || replacing;
  const isBusy = isSyncing || saveMutation.isPending || deleteMutation.isPending;

  function handleSaveToken(e: React.FormEvent) {
    e.preventDefault();
    setTokenTouched(true);
    if (validation !== 'ok' || isBusy) return;
    deleteMutation.reset();
    saveMutation.mutate(tokenInput.trim());
  }

  // ── Aquarium mapping ──────────────────────────────────────────────────────
  const aquariums = connection?.aquariums ?? [];
  const storedAquarium = connection?.externalAquariumId ?? status?.externalAquariumId ?? null;
  const preselected = storedAquarium && aquariums.some((a) => a.id === storedAquarium)
    ? storedAquarium
    : aquariums.length === 1 ? aquariums[0]?.id ?? '' : '';
  const selectedAquarium = aquariumChoice ?? preselected;
  const selectedAquariumName = aquariums.find((a) => a.id === selectedAquarium)?.name ?? '';

  // ── Sync ──────────────────────────────────────────────────────────────────
  const canSync = !!aquariumId && hasToken && partnerKeyConfigured && !!connection && selectedAquarium !== '' && !isBusy;

  async function handleSync() {
    if (!aquariumId || !canSync) return;
    setIsSyncing(true);
    setSyncError(null);
    setLogEntries([]);
    appendLog('info', t('integrations.faunaMarin.log.connecting'));
    try {
      await syncFaunaMarinAnalyses(aquariumId, selectedAquarium, (event) => {
        switch (event.type) {
          case 'authenticated':
            appendLog('success', t('integrations.faunaMarin.log.authenticated', { name: event.account.displayName, aquarium: event.aquariumName }));
            break;
          case 'list':
            appendLog('info', event.toImport === 0
              ? t('integrations.faunaMarin.log.nothingNew')
              : t('integrations.faunaMarin.log.list', { total: event.total, toImport: event.toImport, skipped: event.skipped }));
            break;
          case 'analysis_done':
            appendLog('success', t(
              event.action === 'updated' ? 'integrations.faunaMarin.log.analysisUpdated' : 'integrations.faunaMarin.log.analysisImported',
              { date: event.reportDate ?? event.date, params: event.parametersFound },
            ));
            break;
          case 'analysis_failed':
            appendLog('error', t('integrations.faunaMarin.log.analysisFailed', { date: event.date }));
            break;
          case 'sync_failed': {
            const described = describeFaunaMarinError(event.code);
            appendLog('error', t(described.key, described.values));
            break;
          }
          case 'done':
            if (event.aborted) {
              appendLog('error', t('integrations.faunaMarin.log.aborted'));
            } else {
              appendLog(event.failed > 0 ? 'warning' : 'success', t('integrations.faunaMarin.log.done', {
                imported: event.imported, updated: event.updated, skipped: event.skipped, failed: event.failed,
              }));
              if (event.imported + event.updated > 0) {
                void queryClient.invalidateQueries({ queryKey: ['icp-analyses', aquariumId] });
                void queryClient.invalidateQueries({ queryKey: ['measurements', aquariumId] });
                showToast(t('integrations.faunaMarin.successMessage', { imported: event.imported, updated: event.updated }), event.failed > 0 ? 'info' : 'success');
              }
            }
            break;
        }
      });
    } catch (err) {
      const uiError = toFaunaMarinUiError(err);
      if (uiError.legacyCredentialsRemoved) setLegacyNotice(true);
      setSyncError(uiError);
      appendLog('error', t(uiError.key, uiError.values));
    } finally {
      setIsSyncing(false);
      void queryClient.invalidateQueries({ queryKey: FAUNA_MARIN_STATUS_QUERY_KEY });
    }
  }

  function lastSyncText(lastSync: IntegrationLastSync): string {
    return t('integrations.faunaMarin.sync.summary', {
      imported: lastSync.imported, updated: lastSync.updated, skipped: lastSync.skipped, failed: lastSync.failed,
    });
  }

  // ── Render ────────────────────────────────────────────────────────────────
  if (statusQuery.isPending) {
    return (
      <Card variant="outlined" className="fauna-marin-panel">
        <Heading level={2}>{t('integrations.faunaMarin.title')}</Heading>
        <LiveRegion><Text color="secondary">{t('integrations.faunaMarin.loading')}</Text></LiveRegion>
      </Card>
    );
  }

  if (statusQuery.isError) {
    return (
      <Card variant="outlined" className="fauna-marin-panel">
        <Heading level={2}>{t('integrations.faunaMarin.title')}</Heading>
        <LoadErrorState
          title={t('integrations.faunaMarin.loadError')}
          titleLevel={3}
          onRetry={() => { void statusQuery.refetch(); }}
          retryLabel={t('integrations.faunaMarin.retry')}
          retrying={statusQuery.isFetching}
        />
      </Card>
    );
  }

  const announcement = saveMutation.isPending
    ? t('integrations.faunaMarin.token.saving')
    : deleteMutation.isPending
      ? t('integrations.faunaMarin.token.removing')
      : saveMutation.isSuccess
        ? t('integrations.faunaMarin.token.savedSuccess')
        : deleteMutation.isSuccess
          ? t('integrations.faunaMarin.token.removed')
          : connectionQuery.isFetching && hasToken
            ? t('integrations.faunaMarin.connection.verifying')
            : '';

  return (
    <Card variant="outlined" className="fauna-marin-panel">
      <Stack gap="2">
        <Heading level={2}>{t('integrations.faunaMarin.title')}</Heading>
        <Text color="secondary">{t('integrations.faunaMarin.description')}</Text>
      </Stack>

      <LiveRegion visuallyHidden>{announcement}</LiveRegion>

      {!partnerKeyConfigured && (
        <Alert tone="info" title={t('integrations.faunaMarin.notEnabled.title')} live="off">
          {t('integrations.faunaMarin.notEnabled.body')}
        </Alert>
      )}

      {legacyNotice && !hasToken && (
        <Alert tone="warning" title={t('integrations.faunaMarin.legacyRemoved.title')} live="polite" onDismiss={() => setLegacyNotice(false)} dismissLabel={t('integrations.faunaMarin.dismiss')}>
          {t('integrations.faunaMarin.legacyRemoved.body')}
        </Alert>
      )}

      {/* ── API token ───────────────────────────────────────────────────── */}
      <section className="fauna-marin-panel__section" aria-labelledby={`${tokenInputId}-heading`}>
        <Heading level={3} id={`${tokenInputId}-heading`}>{t('integrations.faunaMarin.token.heading')}</Heading>

        {showTokenForm ? (
          <form className="fauna-marin-panel__form" onSubmit={handleSaveToken} noValidate>
            <div className="fauna-marin-panel__token-row">
              <Input
                id={tokenInputId}
                className="fauna-marin-panel__token-input"
                type={showToken ? 'text' : 'password'}
                label={t('integrations.faunaMarin.token.label')}
                value={tokenInput}
                onChange={(e) => { setTokenInput(e.target.value); }}
                onBlur={() => { if (tokenInput !== '') setTokenTouched(true); }}
                error={tokenError}
                helperText={t('integrations.faunaMarin.token.help', { length: FAUNA_MARIN_TOKEN_LENGTH })}
                disabled={isBusy}
                required
                autoComplete="off"
                spellCheck={false}
                maxLength={FAUNA_MARIN_TOKEN_LENGTH + 16}
                fullWidth
              />
              <Button
                type="button"
                variant="secondary"
                className="fauna-marin-panel__reveal"
                aria-pressed={showToken}
                aria-controls={tokenInputId}
                onClick={() => setShowToken((v) => !v)}
                disabled={isBusy}
              >
                {showToken ? t('integrations.faunaMarin.token.hide') : t('integrations.faunaMarin.token.show')}
              </Button>
            </div>
            <Text variant="body-sm" color="secondary">
              <a className="fauna-marin-panel__link" href={FAUNA_MARIN_TOKEN_URL} target="_blank" rel="noopener noreferrer">
                {t('integrations.faunaMarin.token.createLink')}
                {` ${t('integrations.faunaMarin.token.opensInNewTab')}`}
              </a>
            </Text>

            {saveMutation.isError && <Alert compact>{t('integrations.faunaMarin.token.saveError')}</Alert>}

            <Inline gap="3" wrap>
              <Button type="submit" variant={hasToken ? 'secondary' : 'primary'} isLoading={saveMutation.isPending} disabled={isBusy}>
                {t('integrations.faunaMarin.token.save')}
              </Button>
              {replacing && (
                <Button type="button" variant="ghost" disabled={isBusy} onClick={() => { setReplacing(false); setTokenInput(''); setTokenTouched(false); saveMutation.reset(); }}>
                  {t('integrations.faunaMarin.token.cancelReplace')}
                </Button>
              )}
            </Inline>
          </form>
        ) : (
          <Stack gap="3">
            <Text>
              <span className="fauna-marin-panel__mask" aria-hidden="true">{status?.tokenMask ?? '••••'}</span>{' '}
              {t('integrations.faunaMarin.token.saved')}
            </Text>
            {status?.updatedAt && (
              <Text variant="body-sm" color="secondary">
                {t('integrations.faunaMarin.token.savedAt', { date: formatDateTime(status.updatedAt, i18n.language) })}
              </Text>
            )}
            {deleteMutation.isError && <Alert compact>{t('integrations.faunaMarin.token.removeError')}</Alert>}
            <Inline gap="3" wrap>
              <Button type="button" variant="ghost" disabled={isBusy} onClick={() => { setReplacing(true); deleteMutation.reset(); }}>
                {t('integrations.faunaMarin.token.replaceButton')}
              </Button>
              <Button type="button" variant="ghost" disabled={isBusy} isLoading={deleteMutation.isPending} onClick={() => { saveMutation.reset(); deleteMutation.mutate(); }}>
                {t('integrations.faunaMarin.token.remove')}
              </Button>
            </Inline>
          </Stack>
        )}
      </section>

      {/* ── Connection & sync ───────────────────────────────────────────── */}
      {hasToken && (
        <section className="fauna-marin-panel__section" aria-labelledby={`${tokenInputId}-sync-heading`}>
          <Heading level={3} id={`${tokenInputId}-sync-heading`}>{t('integrations.faunaMarin.connection.heading')}</Heading>

          {connection && (
            <Alert tone="success" compact live="off">
              {t('integrations.faunaMarin.connection.connectedAs', { name: connection.account.displayName })}
            </Alert>
          )}
          {connectionError && (
            <Alert tone={connectionError.tone} compact live="polite">{t(connectionError.key, connectionError.values)}</Alert>
          )}

          {connection && aquariums.length === 0 && (
            <EmptyState
              icon={<Icon name="droplet" />}
              headline={t('integrations.faunaMarin.aquarium.emptyTitle')}
              description={t('integrations.faunaMarin.aquarium.emptyBody')}
              actionLabel={t('integrations.faunaMarin.connection.verify')}
              onAction={() => { void connectionQuery.refetch(); }}
              actionVariant="secondary"
              headingLevel={4}
            />
          )}

          {connection && aquariums.length > 0 && (
            <Select
              label={t('integrations.faunaMarin.aquarium.label')}
              hint={t('integrations.faunaMarin.aquarium.help')}
              placeholder={t('integrations.faunaMarin.aquarium.placeholder')}
              value={selectedAquarium}
              onChange={(e) => { setAquariumChoice(e.target.value); }}
              disabled={isBusy}
              fullWidth
              options={aquariums.map((a) => ({
                value: a.id,
                label: a.volume !== null
                  ? t('integrations.faunaMarin.aquarium.optionWithVolume', { name: a.name, volume: a.volume })
                  : a.name,
              }))}
            />
          )}

          {syncError && (
            <Alert tone={syncError.tone} compact>{t(syncError.key, syncError.values)}</Alert>
          )}

          <Inline gap="3" wrap>
            <Button type="button" variant="primary" onClick={() => { void handleSync(); }} disabled={!canSync} isLoading={isSyncing}>
              {isSyncing ? t('integrations.faunaMarin.sync.running') : t('integrations.faunaMarin.sync.button')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => { void connectionQuery.refetch(); }}
              disabled={isBusy || !partnerKeyConfigured || connectionQuery.isFetching}
              isLoading={connectionQuery.isFetching}
            >
              {connectionQuery.isFetching ? t('integrations.faunaMarin.connection.verifying') : t('integrations.faunaMarin.connection.verify')}
            </Button>
          </Inline>
          {selectedAquariumName && (
            <Text variant="body-sm" color="secondary">
              {t('integrations.faunaMarin.sync.target', { aquarium: selectedAquariumName })}
            </Text>
          )}

          <Stack gap="1">
            {status?.lastSync ? (
              <>
                <Text variant="body-sm">
                  {t('integrations.faunaMarin.sync.lastSync', { date: formatDateTime(status.lastSync.at, i18n.language) })}
                </Text>
                <Text variant="body-sm" color="secondary">
                  {status.lastSync.status === 'failed'
                    ? t('integrations.faunaMarin.sync.lastSyncFailed')
                    : lastSyncText(status.lastSync)}
                </Text>
              </>
            ) : (
              <Text variant="body-sm" color="secondary">{t('integrations.faunaMarin.sync.never')}</Text>
            )}
          </Stack>

          <SyncLog entries={logEntries} isRunning={isSyncing} onClear={() => setLogEntries([])} />
        </section>
      )}
    </Card>
  );
}
